import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import { InlineLoader } from '../../components/common/Loader';
import { listStaff, createStaff, updateStaff, deleteStaff } from '../../services/doctorService';

const PERMS = [
  { key: 'appointments',   label: 'Appointments',  icon: 'fa-calendar-alt' },
  { key: 'chat',           label: 'Chat',           icon: 'fa-comment-dots' },
  { key: 'qrScanner',      label: 'QR Scanner',    icon: 'fa-qrcode' },
  { key: 'slotManagement', label: 'Slot Mgmt',     icon: 'fa-clock' },
];

function StaffCard({ staff, onDelete, onEdit }) {
  return (
    <div className="card-p flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
        <i className="fa-solid fa-user text-primary text-base" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary">{staff.username}</p>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {PERMS.filter(p => staff.permissions?.[p.key]).map(p => (
            <span key={p.key} className="badge-primary text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
              <i className={`fa-solid ${p.icon} text-[9px]`} />
              {p.label}
            </span>
          ))}
          {!PERMS.some(p => staff.permissions?.[p.key]) && (
            <span className="text-xs text-text-muted">No permissions set</span>
          )}
        </div>
        <p className="text-[10px] text-danger mt-1 font-medium">
          <i className="fa-solid fa-lock mr-1" />Earnings: Never accessible
        </p>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button onClick={() => onEdit(staff)} className="btn-icon">
          <i className="fa-solid fa-pen text-text-secondary text-sm" />
        </button>
        <button onClick={() => onDelete(staff._id)} className="btn-icon">
          <i className="fa-solid fa-trash text-danger text-sm" />
        </button>
      </div>
    </div>
  );
}

function StaffForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    username: initial?.username || '',
    password: '',
    permissions: {
      appointments:    initial?.permissions?.appointments    || false,
      chat:            initial?.permissions?.chat            || false,
      qrScanner:       initial?.permissions?.qrScanner      || false,
      slotManagement:  initial?.permissions?.slotManagement  || false,
    },
  });
  const [loading, setLoading] = useState(false);

  const toggle = (key) => setForm(p => ({ ...p, permissions: { ...p.permissions, [key]: !p.permissions[key] } }));

  const handleSave = async () => {
    if (!form.username.trim()) { toast.error('Username required'); return; }
    if (!initial && !form.password) { toast.error('Password required for new staff'); return; }
    setLoading(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="bottom-sheet p-5 space-y-4">
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto" />
        <h3 className="text-base font-bold text-text-primary font-heading">
          {initial ? 'Edit Staff' : 'Add Staff Member'}
        </h3>

        <div>
          <label className="input-label">Username</label>
          <input value={form.username} onChange={(e) => setForm(p => ({ ...p, username: e.target.value }))}
            placeholder="Staff username" className="input" disabled={!!initial} />
        </div>

        <div>
          <label className="input-label">{initial ? 'New Password (leave blank to keep)' : 'Password *'}</label>
          <input type="password" value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))}
            placeholder={initial ? 'Leave blank to keep current' : 'Min 6 characters'} className="input" />
        </div>

        <div>
          <label className="input-label mb-2 block">Permissions</label>
          <div className="grid grid-cols-2 gap-2">
            {PERMS.map((p) => (
              <label key={p.key} className={`flex items-center gap-2 p-3 border rounded-xl cursor-pointer transition-all ${
                form.permissions[p.key] ? 'border-primary bg-primary-light' : 'border-border'
              }`}>
                <input type="checkbox" checked={form.permissions[p.key]} onChange={() => toggle(p.key)}
                  className="accent-primary" />
                <i className={`fa-solid ${p.icon} text-primary text-sm`} />
                <span className="text-xs font-medium text-text-primary">{p.label}</span>
              </label>
            ))}
          </div>
          <p className="text-[10px] text-danger mt-2 font-medium text-center">
            <i className="fa-solid fa-lock mr-1" />Earnings is never shareable with staff
          </p>
        </div>

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 border border-border rounded-btn text-sm font-medium text-text-secondary">
            Cancel
          </button>
          <button onClick={handleSave} disabled={loading} className="flex-1 py-2.5 bg-primary text-white rounded-btn text-sm font-semibold">
            {loading ? <i className="fa-solid fa-spinner animate-spin" /> : initial ? 'Save Changes' : 'Create Staff'}
          </button>
        </div>
      </div>
    </>
  );
}

export default function DoctorStaff() {
  const [staff, setStaff]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listStaff();
      setStaff(res.staff || []);
    } catch { toast.error('Failed to load staff'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    await createStaff(form);
    toast.success('Staff account created!');
    load();
  };

  const handleEdit = async (form) => {
    const payload = { permissions: form.permissions };
    if (form.password) payload.password = form.password;
    await updateStaff(editing._id, payload);
    toast.success('Staff updated!');
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this staff account?')) return;
    try {
      await deleteStaff(id);
      toast.success('Staff account removed');
      load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="min-h-screen bg-surface pb-6">
      <Header title="Staff Accounts" showBack />
      <div className="pt-14 px-4 py-4">
        {/* Info banner */}
        <div className="p-3 bg-primary-light border border-primary/20 rounded-xl mb-4">
          <p className="text-xs text-primary font-medium">
            <i className="fa-solid fa-circle-info mr-1.5" />
            Staff can log in using the Doctor login with their username. Earnings section is permanently restricted.
          </p>
        </div>

        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-text-primary">{staff.length} Staff Member{staff.length !== 1 ? 's' : ''}</p>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="flex items-center gap-1.5 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-btn">
            <i className="fa-solid fa-plus text-xs" />Add Staff
          </button>
        </div>

        {loading ? <InlineLoader text="Loading staff..." /> :
         staff.length === 0 ? (
          <div className="text-center py-12">
            <i className="fa-solid fa-users-slash text-3xl text-text-muted mb-2 block" />
            <p className="text-sm text-text-muted">No staff accounts yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {staff.map(s => (
              <StaffCard key={s._id} staff={s}
                onEdit={(s) => { setEditing(s); setShowForm(true); }}
                onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <StaffForm
          initial={editing}
          onSave={editing ? handleEdit : handleCreate}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}
